<template>
  <v-container fluid>
    <!-- Header mit Rücktaste und Titel -->
    <v-card flat dark>
      <v-row no-gutters>
        <v-col cols="12" class="d-flex justify-content-left">
          <v-btn class="actionButton" icon @click="$router.push('/')">
            <v-icon color="white">mdi-chevron-left</v-icon>
          </v-btn>
          <v-card-title class="pl-0 mb-0 py-0 h6 font-weight-bold">Dein Trainingshub</v-card-title>
        </v-col>
      </v-row>
    </v-card>

    <!-- Optional: Bereich für Splits -->
    <v-row no-gutters>
      <v-col>
        <v-card-title class="font-weight-bold pb-0 px-0 h5 white--text"> Direkt ins Training starten </v-card-title>
        <v-slide-group>
          <v-slide-item v-for="(split, index) in workout.splits.workouts" :key="index">
            <v-card
              style="width: 150px; height: 100px"
              class="mx-2 rounded-xl"
              :style="'background: ' + getTrainingPlanVisualization(split.title).color"
              outlined
              @click="selectSplit(split)">
              <v-card-title class="font-weight-bold h5">{{ split.title }}</v-card-title>
              <v-img height="65" width="65" style="position: absolute; bottom: 0; right: 10px" :src="getTrainingPlanVisualization(split.title).img" />
            </v-card>
          </v-slide-item>
        </v-slide-group>
      </v-col>
    </v-row>

    <!-- Vergangene Trainings: Darstellung per TrainingsHubCardStack -->
    <v-row no-gutters v-if="stackedCardSessions.length">
      <v-col cols="12">
        <v-card-title class="font-weight-bold pb-0 px-0 h5 white--text"> Vergangene Trainings </v-card-title>
        <TrainingsHubCardStack :cards="stackedCardSessions" />
      </v-col>
    </v-row>
    <!-- Vergangene Trainings: Darstellung per TrainingsHubCardStack -->
    <v-row no-gutters v-if="stackedCardExercises.length">
      <v-col cols="12">
        <v-card-title class="font-weight-bold pb-0 px-0 h5 white--text"> Deine Übungen </v-card-title>
        <TrainingsHubCardStack exercises :cards="stackedCardExercises" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import api from "@/api"
import TrainingsHubCardStack from "./trainingsHubCardStack.vue"
import { mapGetters, mapActions, mapState } from "vuex"

export default {
  name: "TrainingsHub",
  components: {
    TrainingsHubCardStack,
  },
  data() {
    return {
      workout: null,
      workouts: [],
      groupedSessions: [],
      groupedExercises: [],
    }
  },
  computed: {
    ...mapGetters("deviceStore", ["exerciseList"]),

    // Es werden nur die ersten 5 Sessions mit vorhandener Execution angezeigt.
    stackedCardSessions() {
      const filtered = this.groupedSessions
        .filter((session) => session.execution)
        .map((card) => {
          card.backgroundColor = this.getTrainingPlanVisualization(card.execution.title).color
          card.img = this.getTrainingPlanVisualization(card.execution.title).img
          return card
        })
      return filtered
    },
    stackedCardExercises() {
      const filtered = this.groupedExercises
        .filter((exercise) => exercise.exercise)
        .map((exercise) => {
          exercise.exercise = this.exerciseList.find((ex) => ex._id === exercise.exercise)
          return exercise
        })
      return filtered
    },
  },
  async created() {
    this.workout = await api.fetchOwnWorkout()
    const existingSessions = await api.fetchExistingSessions()
    this.workouts = existingSessions.workouts
    this.groupedSessions = existingSessions.groupedSessions
    this.groupedExercises = existingSessions.groupedExercises
  },
  methods: {
    getTrainingPlanVisualization(title) {
      const armSplitKeywords = [
        // Deutsche Varianten
        "arm",
        "arme",
        "bizeps",
        "trizeps",
        "bizepstraining",
        "trizepstraining",
        "oberarm",
        "unterarm",
        "armtraining",
        "armübung",
        "armübungen",
        "arm-workout",
        "arm-muskulatur",
        // Englische Varianten
        "arm",
        "arms",
        "biceps",
        "triceps",
        "upper arm",
        "forearm",
        "arm training",
        "arm workout",
        "arm exercises",
        "arm muscles",
      ]

      const legSplitKeywords = [
        // Deutsche Varianten
        "bein",
        "beine",
        "beinarbeit",
        "unterkörper",
        "oberschenkel",
        "waden",
        "hamstrings",
        "quadrizeps",
        "beintraining",
        "beinübungen",
        "bein-workout",
        // Englische Varianten
        "leg",
        "legs",
        "leg day",
        "leg workout",
        "leg training",
        "thigh",
        "calves",
        "hamstrings",
        "quads",
        "lower body",
        "leg exercises",
      ]

      const chestSplitKeywords = [
        // Deutsche Varianten
        "brust",
        "push",
        "brusttraining",
        "brustübungen",
        "brust-workout",
        "pectoralis",
        "brustmuskulatur",
        // Englische Varianten
        "chest",
        "chest training",
        "chest workout",
        "pectoral",
        "pecs",
        "chest exercises",
      ]

      const backSplitKeywords = [
        // Deutsche Varianten
        "rücken",
        "pull",
        "rückentraining",
        "rückenübungen",
        "rücken-workout",
        "lats",
        "latissimus",
        "trapez",
        "trapezius",
        // Englische Varianten
        "back",
        "back training",
        "back workout",
        "upper back",
        "lower back",
        "lats",
        "latissimus",
        "trapezius",
        "back exercises",
      ]

      const shoulderSplitKeywords = [
        // Deutsche Varianten
        "schulter",
        "schultertraining",
        "schulterübungen",
        "schulter-workout",
        "deltoid",
        "deltoideus",
        // Englische Varianten
        "shoulder",
        "shoulder training",
        "shoulder workout",
        "deltoid",
        "deltoids",
        "shoulder exercises",
      ]

      const coreSplitKeywords = [
        // Deutsche Varianten
        "bauch",
        "bauchtraining",
        "bauchübungen",
        "bauch-workout",
        "sechpack",
        "magen",
        "core",
        // Englische Varianten
        "abs",
        "abdominals",
        "core",
        "abs workout",
        "ab exercises",
        "sixpack",
        "ober",
      ]

      const lowerName = title.toLowerCase()
      // "#D0F0C0", // Pastellmint
      if (legSplitKeywords.some((keyword) => lowerName.includes(keyword))) {
        return { img: "/img/workout/legSplit.png", color: "#A7C7E7" } // Pfad zum Bild für den Legs split
      }
      if (chestSplitKeywords.some((keyword) => lowerName.includes(keyword))) {
        return { img: "/img/workout/chestSplit.png", color: "#C1E1C1" } // Pfad zum Bild für den Chest split
      }
      if (backSplitKeywords.some((keyword) => lowerName.includes(keyword))) {
        return { img: "/img/workout/backSplit.png", color: "#FFF9C4" } // Pfad zum Bild für den Back split
      }
      if (shoulderSplitKeywords.some((keyword) => lowerName.includes(keyword))) {
        return { img: "/img/workout/backSplit.png", color: "#FFF9C4" } // Pfad zum Bild für den Shoulder split
      }
      if (armSplitKeywords.some((keyword) => lowerName.includes(keyword))) {
        return { img: "/img/workout/armSplit.png", color: "#FADCD9" } // Pfad zum Bild für den Armsplit
      }
      if (coreSplitKeywords.some((keyword) => lowerName.includes(keyword))) {
        return { img: "/img/workout/cardioSplit.png", color: "#FFD1B3" } // Pfad zum Bild für den Core split
      }

      return { img: "/img/workout/upperBodySplit.png", color: "#E3CCE7" } // Standardbild, falls keine Übereinstimmung gefunden wird
    },
    selectSplit(split) {
      console.log("Gewählter Split:", split)
    },
  },
}
</script>

<style scoped>
/* Hier kannst du weitere Styles definieren, falls nötig */
</style>
